import { useState } from 'react';
import { useManproNotifications } from '../../hooks/useManproNotifications';
import { getNotificationIcon } from '../../utils/notificationUtils';
import { formatDistanceToNow } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { Trash2, CheckCircle2, ChevronLeft, ChevronRight, CheckSquare } from 'lucide-react';
import toast from 'react-hot-toast';

const Notifikasi = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const {
    notifications,
    pagination,
    isLoading,
    markAsRead,
    markAllAsRead,
    isMarkingAllAsRead,
    deleteNotification,
    isDeleting,
    bulkDeleteNotifications,
    isBulkDeleting,
  } = useManproNotifications(itemsPerPage, currentPage);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleSelectAll = () => {
    if (selectedIds.length === notifications.length) {
      setSelectedIds([]); // Deselect all
    } else {
      setSelectedIds(notifications.map((n: any) => n.id)); // Select all
    }
  };

  const handleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    if (window.confirm(`Yakin ingin menghapus ${selectedIds.length} notifikasi yang dipilih?`)) {
      bulkDeleteNotifications(selectedIds, {
        onSuccess: () => {
          setSelectedIds([]);
        }
      });
    }
  };

  const handleNextPage = () => {
    if (pagination && currentPage < pagination.last_page) {
      setCurrentPage((prev) => prev + 1);
      setSelectedIds([]);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      setSelectedIds([]);
    }
  };

  return (
    <div className="flex flex-col space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-slate-800">Semua Notifikasi</h1>
          <p className="mt-1 text-sm text-slate-500">Kelola dan lihat riwayat notifikasi Anda.</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
              className="flex items-center gap-2 rounded-xl bg-red-50 text-red-600 px-4 py-2 text-sm font-bold transition hover:bg-red-100 disabled:opacity-50"
            >
              <Trash2 size={16} />
              <span className="hidden sm:inline">Hapus ({selectedIds.length})</span>
            </button>
          )}
          <button
            onClick={() => markAllAsRead()}
            disabled={isMarkingAllAsRead || notifications.length === 0}
            className="flex items-center gap-2 rounded-xl bg-slate-100 text-slate-700 px-4 py-2 text-sm font-bold transition hover:bg-slate-200 disabled:opacity-50"
          >
            <CheckCircle2 size={16} />
            <span className="hidden sm:inline">Tandai Semua Dibaca</span>
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col">
        {/* Header List */}
        <div className="flex items-center border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-3 w-full">
            <button
              onClick={handleSelectAll}
              className="text-slate-400 hover:text-blue-600 transition"
              title="Pilih Semua"
            >
              <CheckSquare size={20} className={selectedIds.length > 0 && selectedIds.length === notifications.length ? "text-blue-600" : ""} />
            </button>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Daftar Notifikasi {pagination && `(${pagination.total})`}
            </span>
          </div>
        </div>

        {/* List Content */}
        <div className="flex-1 divide-y divide-slate-100 bg-white">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-blue-600"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <CheckCircle2 size={32} />
              </div>
              <h3 className="text-lg font-bold text-slate-800">Tidak ada notifikasi</h3>
              <p className="mt-1 text-sm text-slate-500">Anda sudah membaca semua pesan.</p>
            </div>
          ) : (
            notifications.map((item: any) => {
              const Icon = getNotificationIcon(item.type);
              const isSelected = selectedIds.includes(item.id);

              return (
                <div
                  key={item.id}
                  className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 px-4 py-4 transition hover:bg-slate-50 ${
                    !item.is_read ? 'bg-blue-50/30' : ''
                  }`}
                >
                  <div className="flex items-center gap-4 w-full sm:w-auto">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelect(item.id)}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                        !item.is_read ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      <Icon size={18} />
                    </div>
                  </div>

                  <div 
                    className="flex-1 min-w-0 cursor-pointer w-full"
                    onClick={() => { if (!item.is_read) markAsRead(item.id); }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className={`truncate font-extrabold text-sm sm:text-base ${
                        !item.is_read ? 'text-slate-900' : 'text-slate-700'
                      }`}>
                        {item.title}
                      </p>
                      <span className="shrink-0 text-xs text-slate-400 whitespace-nowrap hidden sm:block">
                        {formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: idLocale })}
                      </span>
                    </div>
                    <p className={`mt-1 text-xs sm:text-sm ${!item.is_read ? 'text-slate-600' : 'text-slate-500'} line-clamp-2`}>
                      {item.message}
                    </p>
                    <span className="shrink-0 text-[10px] text-slate-400 mt-2 block sm:hidden">
                      {formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: idLocale })}
                    </span>
                  </div>

                  <div className="flex items-center justify-end w-full sm:w-auto gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Hapus notifikasi ini?')) {
                          deleteNotification(item.id);
                        }
                      }}
                      disabled={isDeleting}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                      title="Hapus"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-3">
            <span className="text-xs sm:text-sm text-slate-500">
              Menampilkan <span className="font-bold">{pagination.from || 0}</span> - <span className="font-bold">{pagination.to || 0}</span> dari <span className="font-bold">{pagination.total}</span>
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-xs sm:text-sm font-bold text-slate-700">
                {currentPage} / {pagination.last_page}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === pagination.last_page}
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifikasi;
